import {
  CardDetailsDto,
  EmployeePaymentMethodType,
  PaymentsProvider,
} from '@zorro/clients';
import { Alert } from '@zorro/design-system/v2/Alert';
import { Stack } from '@zorro/web/design-system';
import { Fragment } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { $enum } from 'ts-enum-util';

import { EmployeePaymentMethodDetails } from '../../EmployeePage/EmployeePageContent/EmployeePageTabs/EmployeePaymentsTab/EmployeePaymentMethodDetails';
import { PaymentAmountDistribution } from '../../EmployeePage/EmployeePageContent/EmployeePageTabs/EmployeePaymentsTab/PaymentAmountDistribution';
import { CreateCombinedPaymentMethod } from '../CreateCombinedPaymentMethod';
import { PaymentDetailsSkeleton } from '../PaymentDetailsSkeleton';
import { SelfPayNotice } from '../SelfPayNotice';
import { CardMethodSection } from '../ZorroPay/CardMethodSection';
import { ZorroPayBankAccount } from '../ZorroPay/ZorroPayBankAccount';
import { PaymentMethodToDisplay } from '../ZorroPay/paymentMethodsToDisplay.utils';
import { PaymentMethodFormFields } from '../usePaymentMethodForm';
import {
  MethodDisplaySpec,
  PaymentMethodViewer,
} from './paymentMethodDisplayRules';
import { usePaymentMethodDisplayData } from './usePaymentMethodDisplayData';

type PaymentDetailsProps = {
  viewer: PaymentMethodViewer;
  employeeId: string;
  employerId: string;
  onboardingPeriodId: string;
  /**
   * Editing flows only — when the type was changed in-form to COMBINED, the
   * distribution editor renders instead of the read-only view. Display-only
   * surfaces omit both.
   */
  paymentMethodForm?: UseFormReturn<PaymentMethodFormFields>;
  hasChangedPaymentMethodType?: boolean;
  withHeader?: boolean;
};

type LynxMethodsProps = {
  employeeId: string;
  data: ReturnType<typeof usePaymentMethodDisplayData>;
};

const UnfundedPurseNotice = () => (
  <Alert
    variant="warning"
    role="note"
    title="Payment details not available yet"
    withCloseButton={false}
  >
    The employee's purse hasn't been funded. Details unlock once the employer's
    contribution settles.
  </Alert>
);

const LynxMethods = ({ employeeId, data }: LynxMethodsProps) => {
  const {
    isLynxEnabled,
    isAccountActive,
    methodSpecs,
    employeeCard,
    opsCard,
    billingAddress,
    paymentNoteInfo,
    isWhoPaysNoteReady,
    isMethodsLoading,
    isImpersonated,
    isFirstAllocationSucceeded,
    openCardReveal,
  } = data;

  if (isAccountActive === undefined) {
    return <PaymentDetailsSkeleton />;
  }
  if (!isAccountActive) {
    return null;
  }

  const hasMultipleMethods = methodSpecs.length > 1;

  const renderCardSection = (
    spec: MethodDisplaySpec,
    card: CardDetailsDto | null,
    label: string,
    cardBillingAddress?: string
  ) => (
    <CardMethodSection
      employeeId={employeeId}
      card={card}
      isLoading={isMethodsLoading}
      label={hasMultipleMethods ? label : 'Card details'}
      billingAddress={hasMultipleMethods ? undefined : cardBillingAddress}
      paymentNoteInfo={
        spec.showWhoPaysNote && isWhoPaysNoteReady && card?.isActive
          ? paymentNoteInfo
          : undefined
      }
      isImpersonated={isImpersonated}
      onReveal={spec.canReveal && card?.isActive ? openCardReveal : undefined}
      isRevealDisabled={!isFirstAllocationSucceeded}
    />
  );

  return (
    <Stack gap="1rem">
      {!isMethodsLoading && !isFirstAllocationSucceeded && (
        <UnfundedPurseNotice />
      )}
      {methodSpecs.map((spec) => (
        <Fragment key={spec.method}>
          {$enum.mapValue(spec.method).with({
            [PaymentMethodToDisplay.ACH]: (
              <ZorroPayBankAccount
                employeeId={employeeId}
                isEnabled={isLynxEnabled}
                isDisabled={!isFirstAllocationSucceeded}
              />
            ),
            [PaymentMethodToDisplay.EMPLOYEE_CARD]: renderCardSection(
              spec,
              employeeCard,
              'Employee card',
              billingAddress
            ),
            [PaymentMethodToDisplay.OPS_CARD]: renderCardSection(
              spec,
              opsCard,
              'Ops card'
            ),
          })}
        </Fragment>
      ))}
    </Stack>
  );
};

export const PaymentDetails = ({
  viewer,
  employeeId,
  employerId,
  onboardingPeriodId,
  paymentMethodForm,
  hasChangedPaymentMethodType = false,
  withHeader = false,
}: PaymentDetailsProps) => {
  // Editing flows drive the type from the form so the display reacts to the
  // selector before saving; the fetched type is the fallback (and the only
  // source on display-only surfaces, which pass no form).
  const formPaymentMethodType = paymentMethodForm?.watch('paymentMethodType');

  const data = usePaymentMethodDisplayData({
    employeeId,
    employerId,
    onboardingPeriodId,
    viewer,
    formPaymentMethodType,
  });

  const paymentMethodType = formPaymentMethodType ?? data.paymentMethodType;

  if (data.isTypeLoading || !paymentMethodType) {
    return <PaymentDetailsSkeleton />;
  }

  const methods =
    data.paymentsProvider === PaymentsProvider.LYNX ? (
      <LynxMethods employeeId={employeeId} data={data} />
    ) : (
      <EmployeePaymentMethodDetails
        employeeId={employeeId}
        onboardingPeriodId={onboardingPeriodId}
        withHeader={withHeader}
      />
    );

  return $enum.mapValue(paymentMethodType).with({
    [EmployeePaymentMethodType.SELF_PAY]: <SelfPayNotice />,
    [EmployeePaymentMethodType.ZORRO_PAY]: methods,
    [EmployeePaymentMethodType.COMBINED]:
      paymentMethodForm && hasChangedPaymentMethodType ? (
        <CreateCombinedPaymentMethod paymentMethodForm={paymentMethodForm} />
      ) : (
        <Stack>
          <PaymentAmountDistribution
            employeeId={employeeId}
            onboardingPeriodId={onboardingPeriodId}
          />
          {methods}
        </Stack>
      ),
  });
};
